// IBAN client-side validation (ISO 13616 mod-97). Server-side validation
// is authoritative — this only catches typos before the round-trip.
//
// Story 8.3 moved this here from `features/payout/` rather than deleting it.
// It is pure, it is tested, and Story 8.5 needs exactly this: re-deriving a
// mod-97 check under delivery pressure is how a validation regression gets
// in. Its server twin (`routes/payout_account.rs::validate_iban`) carries the
// stricter no-echo discipline and is likewise kept, unrouted.

/** Strip whitespace + uppercase. What we send on the wire. */
export function normalizeIban(raw: string): string {
  return raw.replace(/\s+/g, "").toUpperCase();
}

/**
 * Structural + mod-97 checksum validation. Country-specific length tables
 * are deliberately out of scope (the provider enforces them); the generic
 * 15–34 length window plus the checksum rejects virtually all typos.
 */
export function isValidIban(raw: string): boolean {
  const iban = normalizeIban(raw);
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  // Move the country code + check digits to the end, expand letters
  // (A=10 … Z=35), then the whole number mod 97 must equal 1. Digit-wise
  // modulo keeps it in safe-integer range without BigInt.
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const expanded =
      ch >= "A" && ch <= "Z" ? String(ch.charCodeAt(0) - 55) : ch;
    for (const digit of expanded) {
      remainder = (remainder * 10 + (digit.charCodeAt(0) - 48)) % 97;
    }
  }
  return remainder === 1;
}

/** Last four characters — for optimistic display before the server echo. */
export function ibanLast4(raw: string): string {
  return normalizeIban(raw).slice(-4);
}
